import { LoadingSpinner, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, ThemeProvider } from "@mui/material";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { PostShiftReviewFlow } from "@src/appV2/PostShiftReview/PostShiftReviewFlow";
import { type ReactNode, useCallback, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { getCreditProgressBarLabel } from "../../../../EmergencyCredits/utils/getCreditProgressBarLabel";
import { MY_SHIFTS_FULL_PATH } from "../../../path";
import { useEmergencyCreditsClockOutData } from "./hooks/useEmergencyCreditsClockOutData";
import { YourAccountIsRestrictedPage } from "./YourAccountIsRestricted/YourAccountIsRestrictedPage";
import { YourClipboardScorePage } from "./YourClipboardScore/YourClipboardScorePage";
import { YourEmergencyCreditsPage } from "./YourEmergencyCredits/YourEmergencyCreditsPage";

interface RouteParams {
  shiftId: string;
}

interface LocationState {
  facility?: {
    id: string;
    name: string;
    type: string;
  };
  isDelayedWorkplaceReviewEnabled?: boolean;
  backgroundGradient?: string;
  scoreLogs?: ClipboardScoreLog[];
  currentScore?: number;
  scoreChange?: number;
  latenessMinutes: number;
  isSecondShiftInDouble?: boolean;
  isFirstShiftInDouble?: boolean;
  wasBookedWithShortLeadTime?: boolean;
  newlyRestricted?: boolean;
  isArrivalTimeFeatureEnabled?: boolean;
  formattedTargetArrivalTime?: string;
}

type FlowStep = "emergencyCredits" | "clipboardScore" | "restricted";

export function EmergencyCreditsClockOutFlowPage() {
  const { shiftId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const postShiftReviewModalState = useModalState();
  const emergencyCreditsEducationModalState = useModalState();

  const {
    facility,
    isDelayedWorkplaceReviewEnabled,
    backgroundGradient,
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble,
    isFirstShiftInDouble,
    wasBookedWithShortLeadTime,
    newlyRestricted,
    isArrivalTimeFeatureEnabled,
    formattedTargetArrivalTime,
  } = location.state ?? {};

  const [currentStep, setCurrentStep] = useState<FlowStep>("emergencyCredits");

  const {
    emergencyCreditInfo,
    clipboardScoreData,
    restrictionData: { liftTime, reEnrollmentScore, isFinalRestriction },
    isProfileLoading,
  } = useEmergencyCreditsClockOutData({
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble: isSecondShiftInDouble ?? false,
    isFirstShiftInDouble: isFirstShiftInDouble ?? false,
    wasBookedWithShortLeadTime: wasBookedWithShortLeadTime ?? false,
    newlyRestricted: newlyRestricted ?? false,
    isArrivalTimeFeatureEnabled: isArrivalTimeFeatureEnabled ?? false,
    formattedTargetArrivalTime,
  });

  const exitFlow = useCallback((): void => {
    if ((isDelayedWorkplaceReviewEnabled ?? false) || !facility) {
      history.replace(MY_SHIFTS_FULL_PATH);
    } else {
      postShiftReviewModalState.openModal();
    }
  }, [history, isDelayedWorkplaceReviewEnabled, facility, postShiftReviewModalState]);

  const handleEmergencyCreditsContinue = useCallback((): void => {
    setCurrentStep("clipboardScore");
  }, []);

  const handleClipboardScoreContinue = useCallback((): void => {
    if (newlyRestricted === true && isDefined(reEnrollmentScore)) {
      setCurrentStep("restricted");
    } else {
      exitFlow();
    }
  }, [newlyRestricted, reEnrollmentScore, exitFlow]);

  const handlePostShiftReviewComplete = useCallback((): void => {
    postShiftReviewModalState.closeModal();
    history.replace(MY_SHIFTS_FULL_PATH);
  }, [history, postShiftReviewModalState]);

  const handleLearnMoreClick = useCallback((): void => {
    emergencyCreditsEducationModalState.openModal();
  }, [emergencyCreditsEducationModalState]);

  if (isProfileLoading) {
    return (
      <ThemeProvider theme={getTheme()}>
        <Box
          role="progressbar"
          aria-label="Loading emergency credits"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <LoadingSpinner size="medium" />
        </Box>
      </ThemeProvider>
    );
  }

  if (!isDefined(emergencyCreditInfo)) {
    history.replace(MY_SHIFTS_FULL_PATH);
    return null;
  }

  const {
    cancellationCreditStats,
    latenessCreditStats,
    areBothCreditsMaxed,
    canUseCredit,
    hasAvailableLatenessCredit,
    gainedCancellationCredit,
    gainedLatenessCredit,
  } = emergencyCreditInfo;

  const isWorkerLate = isDefined(latenessMinutes) && latenessMinutes > 0;

  let stepContent: ReactNode;

  if (currentStep === "restricted") {
    stepContent = (
      <YourAccountIsRestrictedPage
        isFinalRestriction={isFinalRestriction}
        liftTime={liftTime}
        reEnrollmentScore={reEnrollmentScore!}
        onContinue={exitFlow}
      />
    );
  } else if (currentStep === "clipboardScore" || areBothCreditsMaxed) {
    if (!isDefined(clipboardScoreData)) {
      history.replace(MY_SHIFTS_FULL_PATH);
      return null;
    }

    stepContent = (
      <YourClipboardScorePage
        showCreditProgressBar={isWorkerLate}
        startScore={clipboardScoreData.startScore}
        scoreChanges={clipboardScoreData.scoreChanges}
        subtitle={clipboardScoreData.subtitle}
        doubleShiftNote={clipboardScoreData.doubleShiftNote}
        gradientVariant={clipboardScoreData.gradientVariant}
        creditProgressBarProps={{
          completedCount: emergencyCreditInfo.completedShiftsToDisplayForLatenessCredit,
          requiredCount: latenessCreditStats.requiredShiftsForNextCredit,
          label: getCreditProgressBarLabel("lateness", hasAvailableLatenessCredit),
          color: "green",
          mode: "static",
        }}
        creditAmount={clipboardScoreData.savedPoints}
        canUseCredit={canUseCredit}
        autoApplyCredit={newlyRestricted === true && canUseCredit}
        onContinue={handleClipboardScoreContinue}
      />
    );
  } else {
    stepContent = (
      <YourEmergencyCreditsPage
        backgroundGradient={backgroundGradient}
        cancellationStats={cancellationCreditStats}
        latenessStats={latenessCreditStats}
        gainedCancellationCredit={gainedCancellationCredit}
        gainedLatenessCredit={gainedLatenessCredit}
        onContinue={handleEmergencyCreditsContinue}
        onLearnMoreClick={handleLearnMoreClick}
      />
    );
  }

  return (
    <ThemeProvider theme={getTheme()}>
      {stepContent}
      {facility && (
        <PostShiftReviewFlow
          modalState={postShiftReviewModalState}
          facility={{
            id: facility.id,
            name: facility.name,
            type: facility.type,
            photo: undefined,
          }}
          shiftId={shiftId}
          onComplete={handlePostShiftReviewComplete}
        />
      )}
    </ThemeProvider>
  );
}
